import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Lightbulb, GraduationCap, Loader2, Bot } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { PortfolioAnalysis } from '@/types/portfolio';

interface AIInsightCardProps {
  analysis: PortfolioAnalysis;
  className?: string;
}

interface Narrative {
  compliment: string;
  opportunity: string;
  education: string;
}

export function AIInsightCard({ analysis, className }: AIInsightCardProps) {
  const [narrative, setNarrative] = useState<Narrative | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const generateNarrative = async () => {
      setLoading(true);
      setError(null);
      try {
        // Calculate realized return if cost basis is available
        const totalReturn = analysis.totalCost > 0 
          ? ((analysis.totalValue - analysis.totalCost) / analysis.totalCost) * 100 
          : analysis.expectedReturn;

        // Construct metrics object
        const userMetrics = {
          totalReturn: parseFloat(totalReturn.toFixed(2)),
          volatility: analysis.volatility,
          sharpeRatio: analysis.sharpeRatio
        };

        // Mock benchmark for now (can be passed in props later)
        const benchmarkMetrics = {
          totalReturn: 8.5, // S&P 500 avg
          volatility: 15.0
        };

        const { data, error } = await supabase.functions.invoke('generate-portfolio-narrative', {
          body: { userMetrics, benchmarkMetrics },
        });

        if (error) throw error;

        setNarrative(data);
      } catch (err) {
        console.error('Failed to generate narrative:', err);
        setError('Unable to generate AI insights at this time.');
      } finally {
        setLoading(false);
      }
    };

    if (analysis) {
      generateNarrative();
    }
  }, [analysis]); // Re-run if analysis changes significantly

  if (error) return null; // Hide card on error

  return (
    <Card className={className}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          AI Portfolio Companion
        </CardTitle>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-3">
            <div className="h-16 w-full bg-muted/50 rounded-lg animate-pulse" />
            <div className="h-16 w-full bg-muted/50 rounded-lg animate-pulse" />
            <div className="h-16 w-full bg-muted/50 rounded-lg animate-pulse" />
          </div>
        ) : narrative ? (
          <>
            {/* Compliment Section */}
            <div className="flex gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <Sparkles className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-green-700 mb-1">Going Well</h4>
                <p className="text-sm text-green-800/90 leading-relaxed">{narrative.compliment}</p>
              </div>
            </div>

            {/* Opportunity Section */}
            <div className="flex gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <Lightbulb className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-700 mb-1">Observation</h4>
                <p className="text-sm text-blue-800/90 leading-relaxed">{narrative.opportunity}</p>
              </div>
            </div>

            {/* Education Section */}
            <div className="flex gap-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <GraduationCap className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-purple-700 mb-1">Knowledge Base</h4>
                <p className="text-sm text-purple-800/90 leading-relaxed">{narrative.education}</p>
              </div>
            </div>
            
            <div className="text-[10px] text-center text-muted-foreground pt-2">
              AI-generated for educational purposes only. Not investment advice.
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
